import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { IntegerQuestionOptions, isRationalQuestion, ProblemData } from "./Constants.ts";
import { generateRationalPages } from "./RationalGenerator.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Integer problem generation. See ADR 0030.
 *
 * Everything here is one line of text, and that is the whole point of the ADR:
 * `-7 + 3` is written horizontally because the sign belongs to the number, and
 * column arithmetic is a place-value algorithm with no column for a sign. No
 * value produced here ever reaches `Problems-UI/MathFacts.tsx`
 * (ADR 0030 §2), which is what keeps `RegroupingCheck`, the long division
 * bracket and the carry boxes away from a minus they cannot handle.
 * `__tests__/NegativeBoundary.test.ts` is the guard on that.
 *
 * Unlike the algebra generator, there is no constraint to satisfy: every signed
 * sum, difference and product is a valid integer, and negative *answers* are
 * the subject rather than something to avoid. Division is the one exception and
 * is built backwards from divisor and quotient so it divides exactly - the same
 * trick `buildDivision` uses for whole numbers.
 */

/** Which question types a setting asks for. Mixed means all four. */
function questionTypes(question: IntegerQuestionOptions): IntegerQuestionOptions[] {
    if (question === IntegerQuestionOptions.Mixed) {
        return [
            IntegerQuestionOptions.AddSubtract,
            IntegerQuestionOptions.MultiplyDivide,
            IntegerQuestionOptions.Compare,
            IntegerQuestionOptions.AbsoluteValue,
        ];
    }
    return [question];
}

/**
 * The largest magnitude a problem may be built from.
 *
 * Clamped for the same reason the algebra generator clamps: a setting arriving
 * below 2 would reach getRandomInt with its arguments the wrong way round.
 */
function usableMax(maxValue: number): number {
    return Math.max(2, Math.floor(maxValue) || 2);
}

/**
 * An integer between -max and max, of at least `least` magnitude.
 *
 * Zero is always left out: it makes a dull problem, an invalid divisor, and a
 * `-0`. Multiplication asks for a floor of 2 as well - `12 × (-1)` tests the
 * sign rule and nothing else, and a first draft printed several a page.
 */
function signedInt(max: number, least = 1): number {
    const magnitude = getRandomInt(Math.min(least, max), max);
    return getRandomInt(0, 1) === 0 ? -magnitude : magnitude;
}

/**
 * How an operand is written (ADR 0030 §4).
 *
 * Leading, a negative is bare: `-7 + 3`. After an operator it is
 * parenthesised: `5 - (-3)`, `4 × (-6)`. `5 - -3` is exactly the thing students
 * are being taught not to write.
 */
function operand(value: number, leading: boolean): string {
    if (leading || value >= 0) {
        return String(value);
    }
    return `(${value})`;
}

function buildAddSubtract(max: number): { prompt: string; answer: string } {
    const first = signedInt(max);
    const second = signedInt(max);
    const adding = getRandomInt(0, 1) === 0;
    const result = adding ? first + second : first - second;

    return {
        prompt: `${operand(first, true)} ${adding ? "+" : "-"} ${operand(second, false)}`,
        answer: String(result),
    };
}

function buildMultiplyDivide(max: number): { prompt: string; answer: string } {
    if (getRandomInt(0, 1) === 0) {
        const first = signedInt(max, 2);
        const second = signedInt(max, 2);
        return {
            prompt: `${operand(first, true)} × ${operand(second, false)}`,
            answer: String(first * second),
        };
    }

    // Backwards from divisor and quotient, so the division is always exact.
    // The divisor's magnitude is held down because it is what a student divides
    // by; the dividend is allowed to be as large as that makes it.
    const divisor = signedInt(Math.min(12, max), 2);
    const quotient = signedInt(max, 2);
    return {
        prompt: `${operand(divisor * quotient, true)} ÷ ${operand(divisor, false)}`,
        answer: String(quotient),
    };
}

/**
 * What sits between the two values of a comparison prompt. The renderer splits
 * on a run of whitespace rather than on this exact string, so the two cannot
 * drift apart over a spacing tweak; `IntegerGenerator.test.ts` asserts a
 * comparison prompt still yields exactly two integers.
 */
const COMPARE_SEPARATOR = "    ";

/**
 * Two integers to compare. The answer is the symbol, matching how
 * `RoundingComparingGenerator` states a comparison (ADR 0006).
 *
 * Equality is reachable - both draws can land on the same value - and is left
 * in, because a sheet on which `=` never appears teaches that it never happens.
 */
function buildCompare(max: number): { prompt: string; answer: string } {
    const first = signedInt(max);
    const second = signedInt(max);
    const symbol = first < second ? "<" : first > second ? ">" : "=";

    return { prompt: `${first}${COMPARE_SEPARATOR}${second}`, answer: symbol };
}

/**
 * Absolute value, bare or with one operation after it.
 *
 * The result may be negative - `|3| - 8` is `-5` - and that is not a defect
 * here the way it would be on an algebra sheet. Negative answers are the
 * subject.
 */
function buildAbsoluteValue(max: number): { prompt: string; answer: string } {
    if (getRandomInt(0, 1) === 0) {
        // Bare, and always negative inside. `|10| = 10` asks nothing at all,
        // and a first draft drew the sign at random, so half the bare problems
        // on a sheet were that. Positives still appear on the sheet, in the
        // arm below.
        const magnitude = getRandomInt(1, max);
        return { prompt: `|-${magnitude}|`, answer: String(magnitude) };
    }

    const inner = signedInt(max);
    const magnitude = Math.abs(inner);
    const second = signedInt(max);
    const adding = getRandomInt(0, 1) === 0;
    const result = adding ? magnitude + second : magnitude - second;

    return {
        prompt: `|${inner}| ${adding ? "+" : "-"} ${operand(second, false)}`,
        answer: String(result),
    };
}

/**
 * One integer problem. Always succeeds - there is no draw to reject.
 *
 * `display` is `[prompt, answer]`; `integerQuestion` tells the renderer which
 * shape the prompt is, because a comparison puts its blank between the two
 * values rather than after them. Per-format data goes in its own field and
 * never `numbers[3]`.
 */
export function buildIntegerProblem(
    question: IntegerQuestionOptions,
    maxValue: number,
    key: number
): ProblemData {
    const max = usableMax(maxValue);
    let built: { prompt: string; answer: string };

    switch (question) {
        case IntegerQuestionOptions.MultiplyDivide:
            built = buildMultiplyDivide(max);
            break;
        case IntegerQuestionOptions.Compare:
            built = buildCompare(max);
            break;
        case IntegerQuestionOptions.AbsoluteValue:
            built = buildAbsoluteValue(max);
            break;
        default:
            built = buildAddSubtract(max);
            break;
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        integerQuestion: question,
    };
}

/** A page-shaped set of integer problems. */
export function generateIntegerPages(settings: ISettingsProps): ProblemData[][] | null {
    // The rational types are decimals and signed fractions, and not part of
    // Mixed (ADR 0122 §1): their own generator, which shares only the notation.
    if (isRationalQuestion(settings.IntegerQuestion)) {
        return generateRationalPages(settings, usableMax(settings.IntegerMaxValue ?? 12));
    }
    const types = questionTypes(settings.IntegerQuestion ?? IntegerQuestionOptions.Mixed);
    const maxValue = settings.IntegerMaxValue ?? 12;
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildIntegerProblem(question, maxValue, key),
        fingerprint: (problem) => problem.display![0],
    });
}
